/**
 * TabSwitcher Component
 *
 * A reusable tab switcher: rounded segments in a grey box (`segmented`), or page-level
 * underline tabs over a full-width line (`underline`).
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { Icon } from './icons/Icon';
import { IconName } from '../icons/names';
import { cn } from '../utils';

export interface TabSwitcherItem<T extends string = string> {
  id: T;
  label: string;
  icon?: IconName;
  /** Rendered after the label — e.g. a count badge. */
  badge?: ReactNode;
}

/** `segmented` switches views inside a section; `underline` is the navigation of a page. */
export type TabSwitcherVariant = 'segmented' | 'underline';

export interface TabSwitcherProps<T extends string = string> {
  items: TabSwitcherItem<T>[];
  activeTab: T;
  onTabChange: (tabId: T) => void;
  variant?: TabSwitcherVariant;
  className?: string;
  testId?: string;
}

const tabListVariants = cva('flex', {
  variants: {
    variant: {
      segmented: 'w-full p-px rounded-2xl bg-background-muted',
      underline: 'gap-lg sm:gap-xl border-b border-border-base'
    }
  },
  defaultVariants: { variant: 'segmented' }
});

const tabVariants = cva('transition-all cursor-pointer flex items-center gap-sm', {
  variants: {
    variant: {
      segmented: 'flex-1 px-md py-sm text-sm font-medium rounded-xl justify-center',
      // The 2px underline overlaps the list's 1px line, so the two sit flush.
      underline:
        '-mb-px py-sm text-lg border-b-2 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-base focus-visible:ring-inset'
    },
    active: { true: '', false: '' }
  },
  compoundVariants: [
    { variant: 'segmented', active: true, className: 'bg-white text-foreground shadow-sm' },
    {
      variant: 'segmented',
      active: false,
      className: 'text-foreground-muted hover:text-foreground'
    },
    {
      variant: 'underline',
      active: true,
      className: 'border-foreground-base text-foreground-base font-semibold'
    },
    {
      variant: 'underline',
      active: false,
      className: 'border-transparent text-foreground-muted font-medium hover:text-foreground-base'
    }
  ],
  defaultVariants: { variant: 'segmented', active: false }
});

export function TabSwitcher<T extends string = string>({
  items,
  activeTab,
  onTabChange,
  variant = 'segmented',
  className,
  testId
}: TabSwitcherProps<T>) {
  const iconSize = variant === 'underline' ? 'h-5 w-5' : 'h-4 w-4';
  const listRef = useRef<HTMLDivElement>(null);

  // A scrolling underline row brings the active tab into view, so a narrow screen shows it.
  useEffect(() => {
    if (variant !== 'underline') return;
    const active = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    active?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [variant, activeTab]);

  return (
    <div
      ref={listRef}
      role='tablist'
      data-testid={testId}
      data-variant={variant}
      className={cn(tabListVariants({ variant }), className)}
    >
      {items.map(item => (
        <button
          key={item.id}
          role='tab'
          aria-selected={activeTab === item.id}
          onClick={() => onTabChange(item.id)}
          className={tabVariants({ variant, active: activeTab === item.id })}
        >
          {item.icon && <Icon name={item.icon} className={iconSize} />}
          {item.label}
          {item.badge !== undefined && (
            <span className='inline-flex items-center'>{item.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}
